import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField,
  styled,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useState } from "react";
import { ProductionReportForm } from "./components/ProductionReportForm";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { NavigationContainer } from "@/components/NavigationButton";
import { AddButton } from "@/components/AddButton";
import {
  ProductionTable,
  currentProductionDetails,
} from "./components/ProductionTable";
import { graphql } from "@/config/graphql";

const Body = styled(Box)`
  margin-bottom: 96px;
`;

const HumidityTextField = styled(TextField)`
  margin-bottom: 8px;
`;

const setNoProductionReasonToReportMutation = graphql(`
  mutation setNoProductionReasonToReport(
    $params: SetNoProductionReasonToReportParams!
  ) {
    setNoProductionReasonToReport(params: $params) {
      id
      noProductionReason
    }
  }
`);
const setHumidityToReportMutation = graphql(`
  mutation setHumidityToReport($params: SetHumidityToReportParams!) {
    setHumidityToReport(params: $params) {
      id
      humidity
    }
  }
`);

const NO_PRODUCTION_REASON_OPTIONS = [
  "Domingo, festivo o bajada",
  "Sin solicitud de obra",
  "Planta en panne",
  "Suministros",
  "Clima",
  "Otro",
];

export const ProductionReport = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [dialogForm, setDialogForm] = useState(false);
  const [selectedId, setSelectedId] = useState(0);
  const { data } = useQuery(currentProductionDetails, {
    variables: { params: { id: +(params.id || 0) } },
    onCompleted: (data) => {
      setHumidity(data?.report?.humidity?.toString() ?? null);
      const noProductionReason = data?.report?.noProductionReason;
      setNoProductionReason(noProductionReason ?? null);
      if (noProductionReason) {
        const parsedNoProductionReason = NO_PRODUCTION_REASON_OPTIONS.find(
          (option) => noProductionReason.startsWith(option)
        );
        setNoProductionReason(parsedNoProductionReason ?? null);
        if (parsedNoProductionReason === "Otro") {
          setNoProductionReasonOther(noProductionReason.replace("Otro: ", ""));
        }
      }
    },
  });
  const [humidity, setHumidity] = useState<string | null>(
    data?.report?.humidity?.toString() ?? null
  );
  const [humidityError, setHumidityError] = useState("");
  const [noProductionReason, setNoProductionReason] = useState<string | null>(
    ""
  );
  const [noProductionReasonError, setNoProductionReasonError] = useState("");
  const [noProductionReasonOther, setNoProductionReasonOther] = useState<
    string | null
  >("");
  const [noProductionReasonDialog, setNoProductionReasonDialog] =
    useState(false);
  const [
    setNoProductionReasonToReport,
    { loading: setNoProductionReasonToReportLoading },
  ] = useMutation(setNoProductionReasonToReportMutation);
  const [setHumidityToReport] = useMutation(setHumidityToReportMutation);
  const selected = data?.report?.productionDetails?.find(
    (detail) => detail.id === selectedId
  );

  const goHome = () => navigate("/");
  const goNext = () => {
    navigate(`/formulario-reporte/${params.id}/partidas`);
  };
  const goNextWithValidation = () => {
    const productionDetails = data?.report.productionDetails;
    if (!productionDetails?.length) return setNoProductionReasonDialog(true);
    const reportHumidity = data?.report.humidity;
    if (reportHumidity != humidity) return;
    if (reportHumidity === null || reportHumidity === 0) {
      setHumidityError("Debe ingresar un valor de humedad");
      return;
    }
    goNext();
  };

  return (
    <Body>
      {data?.report.productionDetails?.length ? (
        <HumidityTextField
          label="Cantidad (0 - 100%)"
          fullWidth
          type="text"
          onWheel={() => (document.activeElement as HTMLInputElement).blur()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">% humedad</InputAdornment>
            ),
            inputProps: { inputMode: "decimal" },
          }}
          onChange={(e) => {
            setHumidity(e.target.value);
            setHumidityError("");
          }}
          value={humidity ?? ""}
          onBlur={(e) => {
            const value = e.target.value;
            const newValue = Number.isNaN(Number(value)) ? null : +value;
            setHumidity(newValue?.toString() ?? null);
            setHumidityToReport({
              variables: {
                params: {
                  reportId: +(params.id || 0),
                  humidity: newValue!,
                },
              },
            });
          }}
          error={!!humidityError}
          helperText={humidityError}
        ></HumidityTextField>
      ) : null}
      <ProductionTable
        onEdit={(detail) => {
          setSelectedId(detail.id);
          setDialogForm(true);
        }}
      />
      <AddButton
        variant="contained"
        onClick={() => {
          setDialogForm(true);
          setSelectedId(0);
        }}
      >
        <AddIcon />
        AGREGAR MEZCLA
      </AddButton>
      <NavigationContainer
        handleNext={goNextWithValidation}
        handlePrev={goHome}
      />
      <Dialog
        open={dialogForm}
        onClose={() => setDialogForm(false)}
        fullWidth
        PaperProps={{ sx: { maxWidth: 400 } }}
      >
        <ProductionReportForm
          close={() => setDialogForm(false)}
          defaultValues={{
            id: selected?.id,
            resourceId: selected?.resource?.id,
            sellQuantity: selected?.sellQuantity ?? undefined,
            destinationName: selected?.destination.id
              ? `${selected.destination.name} [${selected.destination.code}]`
              : undefined,
          }}
          destinationDetail={
            selected?.destination.type === "external"
              ? selected?.destination.name ?? undefined
              : undefined
          }
        />
      </Dialog>
      <Dialog
        open={noProductionReasonDialog}
        onClose={() => setNoProductionReasonDialog(false)}
        fullWidth
        PaperProps={{ sx: { maxWidth: 400 } }}
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const reason =
              noProductionReason === "Otro"
                ? `Otro: ${noProductionReasonOther}`
                : noProductionReason ?? "";
            if (!noProductionReason)
              return setNoProductionReasonError("Debe seleccionar un motivo");
            await setNoProductionReasonToReport({
              variables: {
                params: {
                  reportId: +(params.id || 0),
                  noProductionReason: reason,
                },
              },
            });
            goNext();
          }}
        >
          <DialogTitle>Motivo de no producción</DialogTitle>
          <DialogContent>
            <TextField
              select
              label=""
              fullWidth
              error={!!noProductionReasonError}
              helperText={noProductionReasonError}
              value={noProductionReason}
              onChange={(e) => {
                setNoProductionReason(e.target.value);
                setNoProductionReasonError("");
              }}
            >
              <MenuItem value={""} disabled></MenuItem>
              {NO_PRODUCTION_REASON_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
            {noProductionReason === "Otro" ? (
              <TextField
                sx={{ marginTop: 2 }}
                label="Detalle"
                value={noProductionReasonOther ?? ""}
                onChange={(e) => setNoProductionReasonOther(e.target.value)}
                multiline
                rows={4}
                fullWidth
                required
              />
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              type="submit"
              disabled={setNoProductionReasonToReportLoading}
            >
              {setNoProductionReasonToReportLoading ? (
                <CircularProgress sx={{ color: "white" }} size={20} />
              ) : (
                "Guardar"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Body>
  );
};
